/**
 * Converts value to string if it's not already
 */
const normalizeText = (value: any): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join('\n');
  if (value === null || value === undefined) return '';
  return String(value);
};

/**
 * Determines if a translation text should use textarea or input based on length and content
 */
export const shouldUseTextarea = (value: any): boolean => {
  const text = normalizeText(value);

  // Use textarea if:
  // - Text is longer than 80 characters
  // - Text contains multiple lines
  // - Text contains HTML tags
  const hasMultipleLines = text.includes('\n') || text.includes('<br>');
  const hasHTMLTags = /<[^>]+>/.test(text);
  const isLong = text.length > 80;

  return hasMultipleLines || hasHTMLTags || isLong;
};

/**
 * Calculates the number of rows for a textarea based on content
 */
export const calculateTextareaRows = (value: any): number => {
  const text = normalizeText(value);
  const lines = text.split('\n').length;
  const estimatedLines = Math.ceil(text.length / 80);
  const rows = Math.max(lines, estimatedLines);

  // Min 3 rows, max 10 rows
  return Math.min(Math.max(rows, 3), 10);
};

/**
 * Groups translation keys by category (based on key prefix)
 */
export const groupTranslationsByPrefix = (
  translations: Record<string, string>
): Record<string, Record<string, string>> => {
  const groups: Record<string, Record<string, string>> = {};

  Object.entries(translations).forEach(([key, value]) => {
    // Extract prefix (text before first underscore or first capital letter after lowercase)
    const prefix = key.split('_')[0] || 'general';

    if (!groups[prefix]) {
      groups[prefix] = {};
    }

    groups[prefix][key] = value;
  });

  return groups;
};

/**
 * Sorts translation keys alphabetically
 */
export const sortTranslationKeys = (
  translations: Record<string, string>
): [string, string][] => {
  return Object.entries(translations).sort(([keyA], [keyB]) =>
    keyA.localeCompare(keyB)
  );
};

const existingKeySet = (existingKeys: Iterable<string>): Set<string> =>
  existingKeys instanceof Set ? existingKeys : new Set(existingKeys);

/**
 * Validates a new translation key name (trimmed, non-empty, unique; case-sensitive).
 */
export function getNewTranslationKeyError(
  rawKey: string,
  existingKeys: Iterable<string>
): string | null {
  const trimmed = rawKey.trim();
  if (trimmed === '') {
    return 'La chiave non può essere vuota';
  }
  if (existingKeySet(existingKeys).has(trimmed)) {
    return 'Questa chiave esiste già';
  }
  return null;
}

/**
 * Italian text is required for a new key: an empty value is sent on save but the
 * API does not persist the entry (refetch still omits the key).
 */
export function getNewTranslationValueError(rawValue: string): string | null {
  if (rawValue.trim() === '') {
    return 'Inserisci un valore italiano: con testo vuoto la chiave non resta salvata sul server.';
  }
  return null;
}
